import {switchMap} from "rxjs/operators";
import {ofType} from "redux-observable";

import * as actionTypes from "../constants/actionTypes";
import * as componentTypes from "../constants/componentTypes";
import * as config from "../constants/config";
import * as handlers from "../constants/handlers";
import * as methods from "../constants/methods";
import * as resources from "../constants/resources";
import * as getActions from "../actions/getActions";
import * as searchPanelActions from "../actions/searchPanelActions";
import * as service from "../services/searchPanelService";
import * as messageHelper from "../helpers/messageHelper";

const searchPanelInit = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.SEARCH_PANEL_INIT),
        switchMap(({payload}) => {
            if (state$.value.searchPanel[payload.tabId] === undefined) {
                return [];
            }

            if (state$.value.searchPanel[payload.tabId].type === componentTypes.SEARCH_PANEL_COLOR_PALETTE) {
                return [
                    getActions.getProducts(
                        payload.tabId,
                        state$.value.searchPanel[payload.tabId].local,
                        state$.value.settings.fts,
                        state$.value.settings.productGroups,
                        true,
                        handlers.GET_PRODUCTS_BY_PARAMS
                    ),
                    getActions.getFandecks(
                        payload.tabId,
                        state$.value.searchPanel[payload.tabId].local,
                        state$.value.settings.fts,
                        state$.value.settings.fandeckGroups,
                        state$.value.settings.colorTabCompatibleOnly
                    ),
                    getActions.browseColors(
                        payload.tabId,
                        state$.value.settings.colorTabOrderBy,
                        state$.value.settings.colorTabCompatibleOnly,
                        state$.value.settings.colorTabRowCount,
                        state$.value.settings.colorPreviewAsImage
                    )
                ];
            }

            if (state$.value.searchPanel[payload.tabId].type === componentTypes.SEARCH_PANEL_COLOR_SEARCH) {
                return [
                    getActions.getProducts(
                        payload.tabId,
                        state$.value.searchPanel[payload.tabId].local,
                        state$.value.settings.fts,
                        state$.value.settings.productGroups,
                        false,
                        handlers.GET_PRODUCTS_BY_PARAMS
                    ),
                    getActions.getFandecks(
                        payload.tabId,
                        state$.value.searchPanel[payload.tabId].local,
                        state$.value.settings.fts,
                        state$.value.settings.fandeckGroups,
                        false
                    ),
                    getActions.getColors(
                        payload.tabId,
                        state$.value.searchPanel[payload.tabId].local,
                        state$.value.settings.fts,
                        state$.value.settings.searchPanelOrderByDistance,
                        false,
                        handlers.GET_COLORS_BY_PARAMS_INIT,
                        config.MAX_FORMULA_LIMIT,
                        0,
                        state$.value.searchPanel[payload.tabId].formulaListOrder,
                        state$.value.searchPanel[payload.tabId].formulaListAsc
                    )
                ];
            }

            let initRequests = [
                getActions.getProducts(
                    payload.tabId,
                    state$.value.searchPanel[payload.tabId].local,
                    state$.value.settings.fts,
                    state$.value.settings.productGroups,
                    true,
                    handlers.GET_PRODUCTS_BY_PARAMS
                ),
                getActions.getFandecks(
                    payload.tabId,
                    state$.value.searchPanel[payload.tabId].local,
                    state$.value.settings.fts,
                    state$.value.settings.fandeckGroups,
                    true
                ),
                getActions.getColors(
                    payload.tabId,
                    state$.value.searchPanel[payload.tabId].local,
                    state$.value.settings.fts,
                    state$.value.settings.searchPanelOrderByDistance,
                    true,
                    handlers.GET_COLORS_BY_PARAMS_INIT,
                    config.MAX_FORMULA_LIMIT,
                    0,
                    state$.value.searchPanel[payload.tabId].formulaListOrder,
                    state$.value.searchPanel[payload.tabId].formulaListAsc
                )
            ];

            if (state$.value.settings.formulaCode && !state$.value.settings.blendFormulaAndCode) {
                initRequests.push(
                    getActions.getColors(
                        payload.tabId,
                        state$.value.searchPanel[payload.tabId].local,
                        state$.value.settings.fts,
                        state$.value.settings.searchPanelOrderByDistance,
                        true,
                        handlers.GET_COLOR_CODES_BY_PARAMS_INIT,
                        config.MAX_FORMULA_LIMIT,
                        0,
                        "code"
                    )
                );
            }

            if (state$.value.settings.searchPanelBase) {
                initRequests.push(
                    getActions.getBases(
                        payload.tabId,
                        state$.value.searchPanel[payload.tabId].local,
                        state$.value.settings.fts
                    )
                );
            }

            if (state$.value.settings.searchPanelBipp) {
                initRequests.push(
                    getActions.getBipps(
                        payload.tabId,
                        state$.value.searchPanel[payload.tabId].local,
                        state$.value.settings.fts
                    )
                );
            }

            return initRequests;
        })
    );

const selectProduct = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.SELECT_PRODUCT),
        switchMap(({payload}) => {
            service.applySmartFocus(
                payload.tabId,
                state$.value.formula[payload.tabId],
                state$.value.searchPanel[payload.tabId].type,
                state$.value.settings.searchPanelOrder
            );

            if (state$.value.searchPanel[payload.tabId].type === componentTypes.SEARCH_PANEL_COLOR_PALETTE) {
                return [
                    {
                        type: actionTypes.FILTER_FANDECK_LIST,
                        payload: {
                            tabId: payload.tabId,
                            autoselect: payload.autoselect
                        }
                    },
                    {
                        type: actionTypes.FILTER_FORMULA_LIST,
                        payload: {
                            tabId: payload.tabId,
                            autoselect: payload.autoselect
                        }
                    }
                ];
            }

            if (
                service.isSearchPanelValid(
                    state$.value.formula[payload.tabId],
                    state$.value.searchPanel[payload.tabId].type,
                    state$.value.settings.searchPanelOrder
                )
            ) {
                return [
                    {
                        type: actionTypes.SEARCH_PANEL_GET_FORMULA,
                        payload: {
                            tabId: payload.tabId
                        }
                    }
                ];
            }

            if (state$.value.searchPanel[payload.tabId].type === componentTypes.SEARCH_PANEL_COLOR_SEARCH) {
                return [];
            }

            return [
                {
                    type: actionTypes.FILTER_PRODUCT_LIST,
                    payload: {
                        tabId: payload.tabId,
                        autoselect: payload.autoselect
                    }
                },
                {
                    type: actionTypes.FILTER_FANDECK_LIST,
                    payload: {
                        tabId: payload.tabId,
                        autoselect: payload.autoselect
                    }
                },
                {
                    type: actionTypes.FILTER_FORMULA_LIST,
                    payload: {
                        tabId: payload.tabId,
                        autoselect: payload.autoselect
                    }
                },
                {
                    type: actionTypes.FILTER_BASE_LIST,
                    payload: {
                        tabId: payload.tabId,
                        autoselect: payload.autoselect
                    }
                },
                {
                    type: actionTypes.FILTER_BIPP_LIST,
                    payload: {
                        tabId: payload.tabId,
                        autoselect: payload.autoselect
                    }
                }
            ];
        })
    );

const selectFandeck = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.SELECT_FANDECK),
        switchMap(({payload}) => {
            service.applySmartFocus(
                payload.tabId,
                state$.value.formula[payload.tabId],
                state$.value.searchPanel[payload.tabId].type,
                state$.value.settings.searchPanelOrder
            );

            if (state$.value.searchPanel[payload.tabId].type === componentTypes.SEARCH_PANEL_COLOR_PALETTE) {
                return [
                    {
                        type: actionTypes.FILTER_PRODUCT_LIST,
                        payload: {
                            tabId: payload.tabId,
                            autoselect: payload.autoselect
                        }
                    },
                    {
                        type: actionTypes.FILTER_FORMULA_LIST,
                        payload: {
                            tabId: payload.tabId,
                            autoselect: payload.autoselect
                        }
                    }
                ];
            }

            if (
                service.isSearchPanelValid(
                    state$.value.formula[payload.tabId],
                    state$.value.searchPanel[payload.tabId].type,
                    state$.value.settings.searchPanelOrder
                )
            ) {
                return [
                    {
                        type: actionTypes.SEARCH_PANEL_GET_FORMULA,
                        payload: {
                            tabId: payload.tabId
                        }
                    }
                ];
            }

            if (state$.value.searchPanel[payload.tabId].type === componentTypes.SEARCH_PANEL_COLOR_SEARCH) {
                if (
                    state$.value.formula[payload.tabId] === undefined ||
                    state$.value.formula[payload.tabId].color === null
                ) {
                    return [
                        {
                            type: actionTypes.FILTER_FORMULA_LIST,
                            payload: {
                                tabId: payload.tabId,
                                autoselect: payload.autoselect
                            }
                        }
                    ];
                }

                return [
                    {
                        type: actionTypes.FILTER_FORMULA_LIST,
                        payload: {
                            tabId: payload.tabId,
                            autoselect: payload.autoselect
                        }
                    },
                    {
                        type: actionTypes.SERVER_REQUEST,
                        payload: {
                            type: methods.METHOD_GET_COLOR_IN_FANDECK,
                            params: {
                                fandeckId: state$.value.formula[payload.tabId].fandeck.id,
                                colorId: state$.value.formula[payload.tabId].color.id,
                                isLocal: state$.value.formula[payload.tabId].color.local
                            }
                        },
                        method: handlers.GET_COLOR_IN_FANDECK,
                        tabId: payload.tabId
                    }
                ];
            }

            let selectFandeckActions = [
                {
                    type: actionTypes.FILTER_PRODUCT_LIST,
                    payload: {
                        tabId: payload.tabId,
                        autoselect: payload.autoselect
                    }
                },
                {
                    type: actionTypes.FILTER_FANDECK_LIST,
                    payload: {
                        tabId: payload.tabId,
                        autoselect: payload.autoselect
                    }
                },
                {
                    type: actionTypes.FILTER_FORMULA_LIST,
                    payload: {
                        tabId: payload.tabId,
                        autoselect: payload.autoselect
                    }
                },
                {
                    type: actionTypes.FILTER_BASE_LIST,
                    payload: {
                        tabId: payload.tabId,
                        autoselect: payload.autoselect
                    }
                },
                {
                    type: actionTypes.FILTER_BIPP_LIST,
                    payload: {
                        tabId: payload.tabId,
                        autoselect: payload.autoselect
                    }
                }
            ];

            if (
                state$.value.formula[payload.tabId] === undefined ||
                state$.value.formula[payload.tabId].color === null
            ) {
                return selectFandeckActions;
            }

            selectFandeckActions.push({
                type: actionTypes.SERVER_REQUEST,
                payload: {
                    type: methods.METHOD_GET_COLOR_IN_FANDECK,
                    params: {
                        fandeckId: state$.value.formula[payload.tabId].fandeck.id,
                        colorId: state$.value.formula[payload.tabId].color.id,
                        isLocal: state$.value.formula[payload.tabId].color.local
                    }
                },
                method: handlers.GET_COLOR_IN_FANDECK,
                tabId: payload.tabId
            });

            return selectFandeckActions;
        })
    );

const selectFormula = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.SELECT_FORMULA, actionTypes.SELECT_FORMULA_NAME_AND_CODE),
        switchMap(({payload}) => {
            if (
                state$.value.formula[payload.tabId].color === undefined ||
                state$.value.formula[payload.tabId].color === null ||
                state$.value.formula[payload.tabId].color.hasFormula
            ) {
                service.applySmartFocus(
                    payload.tabId,
                    state$.value.formula[payload.tabId],
                    state$.value.searchPanel[payload.tabId].type,
                    state$.value.settings.searchPanelOrder
                );
            }

            if (
                service.isSearchPanelValid(
                    state$.value.formula[payload.tabId],
                    state$.value.searchPanel[payload.tabId].type,
                    state$.value.settings.searchPanelOrder
                )
            ) {
                if (state$.value.searchPanel[payload.tabId].type === componentTypes.SEARCH_PANEL_COLOR_PALETTE) {
                    return [];
                }

                return [
                    {
                        type: actionTypes.SEARCH_PANEL_GET_FORMULA,
                        payload: {
                            tabId: payload.tabId
                        }
                    }
                ];
            }

            if (state$.value.searchPanel[payload.tabId].type === componentTypes.SEARCH_PANEL_COLOR_PALETTE) {
                if (
                    state$.value.login.loggedUser.canEdit(resources.FUNCTION_COMPETITIVE_COLOR_SEARCH) &&
                    state$.value.formula[payload.tabId].color !== null &&
                    !state$.value.formula[payload.tabId].color.hasFormula &&
                    state$.value.formula[payload.tabId].color.fandeckId !== null
                ) {
                    return [searchPanelActions.getClosestColorSearch(payload.tabId)];
                }

                return [];
            }

            if (state$.value.searchPanel[payload.tabId].type === componentTypes.SEARCH_PANEL_COLOR_SEARCH) {
                return [
                    {
                        type: actionTypes.FILTER_FANDECK_LIST,
                        payload: {
                            tabId: payload.tabId,
                            autoselect: payload.autoselect
                        }
                    }
                ];
            }

            return [
                {
                    type: actionTypes.FILTER_PRODUCT_LIST,
                    payload: {
                        tabId: payload.tabId,
                        autoselect: payload.autoselect
                    }
                },
                {
                    type: actionTypes.FILTER_FANDECK_LIST,
                    payload: {
                        tabId: payload.tabId,
                        autoselect: payload.autoselect
                    }
                },
                {
                    type: actionTypes.FILTER_FORMULA_LIST,
                    payload: {
                        tabId: payload.tabId,
                        autoselect: payload.autoselect
                    }
                },
                {
                    type: actionTypes.FILTER_BASE_LIST,
                    payload: {
                        tabId: payload.tabId,
                        autoselect: payload.autoselect
                    }
                },
                {
                    type: actionTypes.FILTER_BIPP_LIST,
                    payload: {
                        tabId: payload.tabId,
                        autoselect: payload.autoselect
                    }
                }
            ];
        })
    );

const selectBase = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.SELECT_BASE),
        switchMap(({payload}) => {
            service.applySmartFocus(
                payload.tabId,
                state$.value.formula[payload.tabId],
                state$.value.searchPanel[payload.tabId].type,
                state$.value.settings.searchPanelOrder
            );

            if (
                service.isSearchPanelValid(
                    state$.value.formula[payload.tabId],
                    state$.value.searchPanel[payload.tabId].type,
                    state$.value.settings.searchPanelOrder
                )
            ) {
                return [
                    {
                        type: actionTypes.SEARCH_PANEL_GET_FORMULA,
                        payload: {
                            tabId: payload.tabId
                        }
                    }
                ];
            }

            if (state$.value.searchPanel[payload.tabId].type !== componentTypes.SEARCH_PANEL_DEFAULT) {
                return [];
            }

            return [
                {
                    type: actionTypes.FILTER_PRODUCT_LIST,
                    payload: {
                        tabId: payload.tabId,
                        autoselect: true
                    }
                },
                {
                    type: actionTypes.FILTER_FANDECK_LIST,
                    payload: {
                        tabId: payload.tabId,
                        autoselect: true
                    }
                },
                {
                    type: actionTypes.FILTER_FORMULA_LIST,
                    payload: {
                        tabId: payload.tabId,
                        autoselect: true
                    }
                },
                {
                    type: actionTypes.FILTER_BASE_LIST,
                    payload: {
                        tabId: payload.tabId,
                        autoselect: true
                    }
                },
                {
                    type: actionTypes.FILTER_BIPP_LIST,
                    payload: {
                        tabId: payload.tabId,
                        autoselect: true
                    }
                }
            ];
        })
    );

const selectBipp = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.SELECT_BIPP),
        switchMap(({payload}) => {
            service.applySmartFocus(
                payload.tabId,
                state$.value.formula[payload.tabId],
                state$.value.searchPanel[payload.tabId].type,
                state$.value.settings.searchPanelOrder
            );

            if (
                service.isSearchPanelValid(
                    state$.value.formula[payload.tabId],
                    state$.value.searchPanel[payload.tabId].type,
                    state$.value.settings.searchPanelOrder
                )
            ) {
                return [
                    {
                        type: actionTypes.SEARCH_PANEL_GET_FORMULA,
                        payload: {
                            tabId: payload.tabId
                        }
                    }
                ];
            }

            if (state$.value.searchPanel[payload.tabId].type !== componentTypes.SEARCH_PANEL_DEFAULT) {
                return [];
            }

            return [
                {
                    type: actionTypes.FILTER_PRODUCT_LIST,
                    payload: {
                        tabId: payload.tabId,
                        autoselect: true
                    }
                },
                {
                    type: actionTypes.FILTER_FANDECK_LIST,
                    payload: {
                        tabId: payload.tabId,
                        autoselect: true
                    }
                },
                {
                    type: actionTypes.FILTER_FORMULA_LIST,
                    payload: {
                        tabId: payload.tabId,
                        autoselect: true
                    }
                },
                {
                    type: actionTypes.FILTER_BASE_LIST,
                    payload: {
                        tabId: payload.tabId,
                        autoselect: true
                    }
                },
                {
                    type: actionTypes.FILTER_BIPP_LIST,
                    payload: {
                        tabId: payload.tabId,
                        autoselect: true
                    }
                }
            ];
        })
    );

const getCalculatedFormula = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.GET_CLOSEST_COLOR),
        switchMap(({payload}) => {
            if (
                state$.value.formula[payload.tabId] === undefined ||
                state$.value.formula[payload.tabId].color === null
            ) {
                return [];
            }

            if (state$.value.formula[payload.tabId].product === null) {
                return [messageHelper.getInfoMessageAction("flashMessage.competitiveSearchSelectProduct")];
            }

            if (state$.value.searchPanel[payload.tabId].type === componentTypes.SEARCH_PANEL_COLOR_SEARCH) {
                return searchPanelActions.getClosestColorAndOpenLoading(
                    payload.tabId,
                    state$.value.formula[payload.tabId].product?.id ?? null,
                    null,
                    state$.value.formula[payload.tabId].color.id,
                    state$.value.formula[payload.tabId].fandeck?.id ?? null,
                    state$.value.formula[payload.tabId].color.local,
                    state$.value.settings.maxToleranceForCompetitiveColors,
                    state$.value.settings.numberOfCompetitiveColors
                );
            }

            return searchPanelActions.getClosestColorAndOpenLoading(
                payload.tabId,
                state$.value.formula[payload.tabId].product?.id ?? null,
                null,
                state$.value.formula[payload.tabId].color.id,
                state$.value.formula[payload.tabId].color.fandeckId,
                false,
                state$.value.settings.maxToleranceForCompetitiveColors,
                state$.value.settings.numberOfCompetitiveColors
            );
        })
    );

const getClosestColor = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.SEARCH_PANEL_GET_FORMULA),
        switchMap(({payload}) => {
            if (state$.value.searchPanel[payload.tabId].type === componentTypes.SEARCH_PANEL_COLOR_SEARCH) {
                return [
                    {
                        type: actionTypes.SERVER_REQUEST,
                        payload: {
                            type: methods.METHOD_GET_FORMULA_DETAIL,
                            params: {
                                productId: state$.value.formula[payload.tabId].product.id,
                                fandeckId: state$.value.formula[payload.tabId].fandeck.id,
                                colorId: state$.value.formula[payload.tabId].color.id,
                                isLocal: state$.value.searchPanel[payload.tabId].local
                            }
                        },
                        method: handlers.GET_CALCULATED_FORMULA_FOR_COLOR_SEARCH,
                        tabId: payload.tabId
                    }
                ];
            }

            return [
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_CALCULATED_FORMULA,
                        params: {
                            productId:
                                state$.value.formula[payload.tabId].product === null
                                    ? null
                                    : state$.value.formula[payload.tabId].product.id,
                            fandeckId: state$.value.formula[payload.tabId].fandeck.id,
                            colorId: state$.value.formula[payload.tabId].color.id,
                            baseId: service.getBaseIdForCalculatedFormula(
                                state$.value.searchPanel[payload.tabId].type,
                                state$.value.formula[payload.tabId].base,
                                state$.value.settings.searchPanelBase
                            ),
                            baseInProductPackageId: service.getBippIdForCalculatedFormula(
                                state$.value.searchPanel[payload.tabId].type,
                                state$.value.formula[payload.tabId].bipp,
                                state$.value.settings.searchPanelBipp
                            ),
                            isLocal: state$.value.searchPanel[payload.tabId].local,
                            historyAllBases: !state$.value.settings.searchPanelBase
                        }
                    },
                    method: handlers.SEARCH_PANEL_GET_CALCULATED_FORMULA,
                    tabId: payload.tabId
                },
                {
                    type: actionTypes.UPDATE_TAB_NAME,
                    payload: {
                        tabId: payload.tabId,
                        color: state$.value.formula[payload.tabId].color.updateLongDisplayName(
                            state$.value.settings.blendFormulaAndCode,
                            state$.value.settings.formulaCodePrimary
                        )
                    }
                }
            ];
        })
    );

const filterProductList = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.FILTER_PRODUCT_LIST),
        switchMap(({payload}) => {
            if (
                state$.value.searchPanel[payload.tabId] === undefined ||
                state$.value.formula[payload.tabId] === undefined ||
                !service.isSearchPanelFilterActive(
                    state$.value.searchPanel[payload.tabId].type,
                    state$.value.settings.searchPanelProduct
                )
            ) {
                return [];
            }

            let responseHandler = handlers.GET_PRODUCTS_BY_PARAMS;
            if (payload.autoselect) {
                responseHandler = handlers.GET_PRODUCTS_WITH_AUTOSELECT;
            }

            let productNameInput = "";
            if (state$.value.formula[payload.tabId].product === null) {
                productNameInput = state$.value.searchPanel[payload.tabId].productNameInput;
            } else if (state$.value.searchPanel[payload.tabId].productNameFilter.trim().length) {
                productNameInput = state$.value.searchPanel[payload.tabId].productNameFilter.trim();
            }

            if (state$.value.searchPanel[payload.tabId].type === componentTypes.SEARCH_PANEL_COLOR_PALETTE) {
                return [
                    getActions.getProductsByParams(
                        payload.tabId,
                        state$.value.settings.colorTabCompatibleOnly &&
                            state$.value.formula[payload.tabId].fandeck !== null
                            ? state$.value.formula[payload.tabId].fandeck.id
                            : null,
                        null,
                        null,
                        productNameInput,
                        state$.value.searchPanel[payload.tabId].local,
                        state$.value.settings.fts,
                        state$.value.settings.productGroups,
                        state$.value.settings.colorTabCompatibleOnly,
                        responseHandler
                    )
                ];
            }

            if (state$.value.searchPanel[payload.tabId].type === componentTypes.SEARCH_PANEL_COLOR_SEARCH) {
                return [
                    getActions.getProductsByParams(
                        payload.tabId,
                        null,
                        null,
                        null,
                        productNameInput,
                        state$.value.searchPanel[payload.tabId].local,
                        state$.value.settings.fts,
                        state$.value.settings.productGroups,
                        false,
                        responseHandler
                    )
                ];
            }

            if (state$.value.settings.searchPanelBase || state$.value.settings.searchPanelBipp) {
                return [
                    getActions.getProductsByParams(
                        payload.tabId,
                        state$.value.formula[payload.tabId].fandeck === null
                            ? null
                            : state$.value.formula[payload.tabId].fandeck.id,
                        state$.value.formula[payload.tabId].color === null
                            ? null
                            : state$.value.formula[payload.tabId].color.id,
                        state$.value.formula[payload.tabId].base === null
                            ? null
                            : state$.value.formula[payload.tabId].base.displayName,
                        productNameInput,
                        state$.value.searchPanel[payload.tabId].local,
                        state$.value.settings.fts,
                        state$.value.settings.productGroups,
                        true,
                        responseHandler
                    )
                ];
            }

            return [
                getActions.getProductsByParams(
                    payload.tabId,
                    state$.value.formula[payload.tabId].fandeck === null
                        ? null
                        : state$.value.formula[payload.tabId].fandeck.id,
                    state$.value.formula[payload.tabId].color === null
                        ? null
                        : state$.value.formula[payload.tabId].color.id,
                    null,
                    productNameInput,
                    state$.value.searchPanel[payload.tabId].local,
                    state$.value.settings.fts,
                    state$.value.settings.productGroups,
                    true,
                    responseHandler
                )
            ];
        })
    );

const filterProductListByName = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.FILTER_PRODUCT_LIST_BY_NAME),
        switchMap(({payload}) => {
            if (
                state$.value.searchPanel[payload.tabId] === undefined ||
                state$.value.formula[payload.tabId] === undefined
            ) {
                return [];
            }

            if (state$.value.searchPanel[payload.tabId].type === componentTypes.SEARCH_PANEL_COLOR_PALETTE) {
                return [
                    getActions.getProductsByParams(
                        payload.tabId,
                        state$.value.settings.colorTabCompatibleOnly &&
                            state$.value.formula[payload.tabId].fandeck !== null
                            ? state$.value.formula[payload.tabId].fandeck.id
                            : null,
                        null,
                        null,
                        payload.productName,
                        state$.value.searchPanel[payload.tabId].local,
                        state$.value.settings.fts,
                        state$.value.settings.productGroups,
                        true,
                        handlers.GET_PRODUCTS_BY_PARAMS
                    )
                ];
            }

            if (state$.value.searchPanel[payload.tabId].type === componentTypes.SEARCH_PANEL_COLOR_SEARCH) {
                return [
                    getActions.getProductsByParams(
                        payload.tabId,
                        null,
                        null,
                        null,
                        payload.productName,
                        state$.value.searchPanel[payload.tabId].local,
                        state$.value.settings.fts,
                        state$.value.settings.productGroups,
                        false,
                        handlers.GET_PRODUCTS_BY_PARAMS
                    )
                ];
            }

            if (state$.value.settings.searchPanelBase || state$.value.settings.searchPanelBipp) {
                return [
                    getActions.getProductsByParams(
                        payload.tabId,
                        state$.value.formula[payload.tabId].fandeck === null
                            ? null
                            : state$.value.formula[payload.tabId].fandeck.id,
                        state$.value.formula[payload.tabId].color === null
                            ? null
                            : state$.value.formula[payload.tabId].color.id,
                        state$.value.formula[payload.tabId].base === null
                            ? null
                            : state$.value.formula[payload.tabId].base.displayName,
                        payload.productName,
                        state$.value.searchPanel[payload.tabId].local,
                        state$.value.settings.fts,
                        state$.value.settings.productGroups,
                        true,
                        handlers.GET_PRODUCTS_BY_PARAMS
                    )
                ];
            }

            return [
                getActions.getProductsByParams(
                    payload.tabId,
                    state$.value.formula[payload.tabId].fandeck === null
                        ? null
                        : state$.value.formula[payload.tabId].fandeck.id,
                    state$.value.formula[payload.tabId].color === null
                        ? null
                        : state$.value.formula[payload.tabId].color.id,
                    null,
                    payload.productName,
                    state$.value.searchPanel[payload.tabId].local,
                    state$.value.settings.fts,
                    state$.value.settings.productGroups,
                    true,
                    handlers.GET_PRODUCTS_BY_PARAMS
                )
            ];
        })
    );

const reloadFandeckList = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.RELOAD_FANDECK_LIST_SEARCH),
        switchMap(({payload}) => {
            if (state$.value.searchPanel[payload.tabId].type === componentTypes.SEARCH_PANEL_COLOR_PALETTE) {
                return [
                    getActions.getFandecksByParams(
                        payload.tabId,
                        state$.value.settings.colorTabCompatibleOnly &&
                            state$.value.formula[payload.tabId].product !== null
                            ? state$.value.formula[payload.tabId].product.id
                            : null,
                        null,
                        null,
                        payload.fandeckName,
                        state$.value.searchPanel[payload.tabId].local,
                        state$.value.settings.fts,
                        state$.value.settings.fandeckGroups,
                        state$.value.settings.colorTabCompatibleOnly,
                        handlers.GET_FANDECKS_WITH_OFFSET,
                        payload.fandeckLimit,
                        payload.fandeckOffset
                    )
                ];
            }

            if (state$.value.searchPanel[payload.tabId].type === componentTypes.SEARCH_PANEL_COLOR_SEARCH) {
                return [
                    getActions.getFandecksByParams(
                        payload.tabId,
                        null,
                        state$.value.formula[payload.tabId].color === null
                            ? null
                            : state$.value.formula[payload.tabId].color.id,
                        null,
                        payload.fandeckName,
                        state$.value.searchPanel[payload.tabId].local,
                        state$.value.settings.fts,
                        state$.value.settings.fandeckGroups,
                        false,
                        handlers.GET_FANDECKS_WITH_OFFSET,
                        payload.fandeckLimit,
                        payload.fandeckOffset
                    )
                ];
            }

            if (state$.value.settings.searchPanelBase || state$.value.settings.searchPanelBipp) {
                return [
                    getActions.getFandecksByParams(
                        payload.tabId,
                        state$.value.formula[payload.tabId].product === null
                            ? null
                            : state$.value.formula[payload.tabId].product.id,
                        state$.value.formula[payload.tabId].color === null
                            ? null
                            : state$.value.formula[payload.tabId].color.id,
                        state$.value.formula[payload.tabId].base === null
                            ? null
                            : state$.value.formula[payload.tabId].base.displayName,
                        payload.fandeckName,
                        state$.value.searchPanel[payload.tabId].local,
                        state$.value.settings.fts,
                        state$.value.settings.fandeckGroups,
                        true,
                        handlers.GET_FANDECKS_WITH_OFFSET,
                        payload.fandeckLimit,
                        payload.fandeckOffset
                    )
                ];
            }

            return [
                getActions.getFandecksByParams(
                    payload.tabId,
                    state$.value.formula[payload.tabId].product === null
                        ? null
                        : state$.value.formula[payload.tabId].product.id,
                    state$.value.formula[payload.tabId].color === null
                        ? null
                        : state$.value.formula[payload.tabId].color.id,
                    null,
                    payload.fandeckName,
                    state$.value.searchPanel[payload.tabId].local,
                    state$.value.settings.fts,
                    state$.value.settings.fandeckGroups,
                    true,
                    handlers.GET_FANDECKS_WITH_OFFSET,
                    payload.fandeckLimit,
                    payload.fandeckOffset
                )
            ];
        })
    );

const filterFandeckList = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.FILTER_FANDECK_LIST),
        switchMap(({payload}) => {
            if (
                state$.value.searchPanel[payload.tabId] === undefined ||
                state$.value.formula[payload.tabId] === undefined
            ) {
                return [];
            }

            let responseHandler = handlers.GET_FANDECKS_BY_PARAMS;
            if (payload.autoselect) {
                responseHandler = handlers.GET_FANDECKS_WITH_AUTOSELECT;
            }

            let fandeckNameInput = "";
            if (state$.value.formula[payload.tabId].fandeck === null) {
                fandeckNameInput = state$.value.searchPanel[payload.tabId].fandeckNameInput;
            } else if (state$.value.searchPanel[payload.tabId].fandeckNameFilter.trim().length) {
                fandeckNameInput = state$.value.searchPanel[payload.tabId].fandeckNameFilter.trim();
            }

            if (state$.value.searchPanel[payload.tabId].type === componentTypes.SEARCH_PANEL_COLOR_PALETTE) {
                return [
                    getActions.getFandecksByParams(
                        payload.tabId,
                        state$.value.settings.colorTabCompatibleOnly &&
                            state$.value.formula[payload.tabId].product !== null
                            ? state$.value.formula[payload.tabId].product.id
                            : null,
                        null,
                        null,
                        fandeckNameInput,
                        state$.value.searchPanel[payload.tabId].local,
                        state$.value.settings.fts,
                        state$.value.settings.fandeckGroups,
                        state$.value.settings.colorTabCompatibleOnly,
                        responseHandler
                    )
                ];
            }

            if (state$.value.searchPanel[payload.tabId].type === componentTypes.SEARCH_PANEL_COLOR_SEARCH) {
                return [
                    getActions.getFandecksByParams(
                        payload.tabId,
                        null,
                        state$.value.formula[payload.tabId].color === null
                            ? null
                            : state$.value.formula[payload.tabId].color.id,
                        null,
                        fandeckNameInput,
                        state$.value.searchPanel[payload.tabId].local,
                        state$.value.settings.fts,
                        state$.value.settings.fandeckGroups,
                        false,
                        responseHandler
                    )
                ];
            }

            if (state$.value.settings.searchPanelBase || state$.value.settings.searchPanelBipp) {
                return [
                    getActions.getFandecksByParams(
                        payload.tabId,
                        state$.value.formula[payload.tabId].product === null
                            ? null
                            : state$.value.formula[payload.tabId].product.id,
                        state$.value.formula[payload.tabId].color === null
                            ? null
                            : state$.value.formula[payload.tabId].color.id,
                        state$.value.formula[payload.tabId].base === null
                            ? null
                            : state$.value.formula[payload.tabId].base.displayName,
                        fandeckNameInput,
                        state$.value.searchPanel[payload.tabId].local,
                        state$.value.settings.fts,
                        state$.value.settings.fandeckGroups,
                        true,
                        responseHandler
                    )
                ];
            }

            return [
                getActions.getFandecksByParams(
                    payload.tabId,
                    state$.value.formula[payload.tabId].product === null
                        ? null
                        : state$.value.formula[payload.tabId].product.id,
                    state$.value.formula[payload.tabId].color === null
                        ? null
                        : state$.value.formula[payload.tabId].color.id,
                    null,
                    fandeckNameInput,
                    state$.value.searchPanel[payload.tabId].local,
                    state$.value.settings.fts,
                    state$.value.settings.fandeckGroups,
                    true,
                    responseHandler
                )
            ];
        })
    );

const filterFandeckListByName = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.FILTER_FANDECK_LIST_BY_NAME),
        switchMap(({payload}) => {
            if (
                state$.value.searchPanel[payload.tabId] === undefined ||
                state$.value.formula[payload.tabId] === undefined
            ) {
                return [];
            }

            if (state$.value.searchPanel[payload.tabId].type === componentTypes.SEARCH_PANEL_COLOR_PALETTE) {
                return [
                    getActions.getFandecksByParams(
                        payload.tabId,
                        state$.value.settings.colorTabCompatibleOnly &&
                            state$.value.formula[payload.tabId].product !== null
                            ? state$.value.formula[payload.tabId].product.id
                            : null,
                        null,
                        null,
                        payload.fandeckName,
                        state$.value.searchPanel[payload.tabId].local,
                        state$.value.settings.fts,
                        state$.value.settings.fandeckGroups,
                        state$.value.settings.colorTabCompatibleOnly,
                        handlers.GET_FANDECKS_BY_PARAMS,
                        payload.fandeckLimit,
                        payload.fandeckOffset
                    )
                ];
            }

            if (state$.value.searchPanel[payload.tabId].type === componentTypes.SEARCH_PANEL_COLOR_SEARCH) {
                return [
                    getActions.getFandecksByParams(
                        payload.tabId,
                        null,
                        state$.value.formula[payload.tabId].color === null
                            ? null
                            : state$.value.formula[payload.tabId].color.id,
                        null,
                        payload.fandeckName,
                        state$.value.searchPanel[payload.tabId].local,
                        state$.value.settings.fts,
                        state$.value.settings.fandeckGroups,
                        false,
                        handlers.GET_FANDECKS_BY_PARAMS,
                        payload.fandeckLimit,
                        payload.fandeckOffset
                    )
                ];
            }

            if (state$.value.settings.searchPanelBase || state$.value.settings.searchPanelBipp) {
                return [
                    getActions.getFandecksByParams(
                        payload.tabId,
                        state$.value.formula[payload.tabId].product === null
                            ? null
                            : state$.value.formula[payload.tabId].product.id,
                        state$.value.formula[payload.tabId].color === null
                            ? null
                            : state$.value.formula[payload.tabId].color.id,
                        state$.value.formula[payload.tabId].base === null
                            ? null
                            : state$.value.formula[payload.tabId].base.displayName,
                        payload.fandeckName,
                        state$.value.searchPanel[payload.tabId].local,
                        state$.value.settings.fts,
                        state$.value.settings.fandeckGroups,
                        true,
                        handlers.GET_FANDECKS_BY_PARAMS,
                        payload.fandeckLimit,
                        payload.fandeckOffset
                    )
                ];
            }

            return [
                getActions.getFandecksByParams(
                    payload.tabId,
                    state$.value.formula[payload.tabId].product === null
                        ? null
                        : state$.value.formula[payload.tabId].product.id,
                    state$.value.formula[payload.tabId].color === null
                        ? null
                        : state$.value.formula[payload.tabId].color.id,
                    null,
                    payload.fandeckName,
                    state$.value.searchPanel[payload.tabId].local,
                    state$.value.settings.fts,
                    state$.value.settings.fandeckGroups,
                    true,
                    handlers.GET_FANDECKS_BY_PARAMS,
                    payload.fandeckLimit,
                    payload.fandeckOffset
                )
            ];
        })
    );

const reloadColorListByName = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.RELOAD_FORMULA_LIST_SEARCH_BY_NAME),
        switchMap(({payload}) => {
            if (state$.value.searchPanel[payload.tabId].type === componentTypes.SEARCH_PANEL_COLOR_SEARCH) {
                return [
                    getActions.getColorsByParams(
                        payload.tabId,
                        null,
                        state$.value.formula[payload.tabId].fandeck !== null
                            ? state$.value.formula[payload.tabId].fandeck.id
                            : null,
                        null,
                        payload.formulaName,
                        null,
                        state$.value.searchPanel[payload.tabId].local,
                        state$.value.settings.fts,
                        state$.value.settings.searchPanelOrderByDistance,
                        false,
                        handlers.GET_COLORS_WITH_OFFSET,
                        payload.formulaLimit,
                        payload.formulaOffset
                    )
                ];
            }

            if (state$.value.settings.searchPanelBase || state$.value.settings.searchPanelBipp) {
                return [
                    getActions.getColorsByParams(
                        payload.tabId,
                        state$.value.formula[payload.tabId].product !== null
                            ? state$.value.formula[payload.tabId].product.id
                            : null,
                        state$.value.formula[payload.tabId].fandeck !== null
                            ? state$.value.formula[payload.tabId].fandeck.id
                            : null,
                        state$.value.formula[payload.tabId].base !== null
                            ? state$.value.formula[payload.tabId].base.displayName
                            : null,
                        payload.formulaName,
                        null,
                        state$.value.searchPanel[payload.tabId].local,
                        state$.value.settings.fts,
                        state$.value.settings.searchPanelOrderByDistance,
                        true,
                        handlers.GET_COLORS_WITH_OFFSET,
                        payload.formulaLimit,
                        payload.formulaOffset
                    )
                ];
            }

            return [
                getActions.getColorsByParams(
                    payload.tabId,
                    state$.value.formula[payload.tabId].product !== null
                        ? state$.value.formula[payload.tabId].product.id
                        : null,
                    state$.value.formula[payload.tabId].fandeck !== null
                        ? state$.value.formula[payload.tabId].fandeck.id
                        : null,
                    null,
                    payload.formulaName,
                    null,
                    state$.value.searchPanel[payload.tabId].local,
                    state$.value.settings.fts,
                    state$.value.settings.searchPanelOrderByDistance,
                    true,
                    handlers.GET_COLORS_WITH_OFFSET,
                    payload.formulaLimit,
                    payload.formulaOffset
                )
            ];
        })
    );

const reloadColorListByCode = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.RELOAD_FORMULA_LIST_SEARCH_BY_CODE),
        switchMap(({payload}) => {
            if (state$.value.settings.searchPanelBase || state$.value.settings.searchPanelBipp) {
                return [
                    getActions.getColorsByParams(
                        payload.tabId,
                        state$.value.formula[payload.tabId].product !== null
                            ? state$.value.formula[payload.tabId].product.id
                            : null,
                        state$.value.formula[payload.tabId].fandeck !== null
                            ? state$.value.formula[payload.tabId].fandeck.id
                            : null,
                        state$.value.formula[payload.tabId].base !== null
                            ? state$.value.formula[payload.tabId].base.displayName
                            : null,
                        null,
                        payload.formulaCode,
                        state$.value.searchPanel[payload.tabId].local,
                        state$.value.settings.fts,
                        state$.value.settings.searchPanelOrderByDistance,
                        true,
                        handlers.GET_COLOR_CODES_WITH_OFFSET,
                        payload.formulaLimit,
                        payload.formulaOffset,
                        "code"
                    )
                ];
            }

            return [
                getActions.getColorsByParams(
                    payload.tabId,
                    state$.value.formula[payload.tabId].product !== null
                        ? state$.value.formula[payload.tabId].product.id
                        : null,
                    state$.value.formula[payload.tabId].fandeck !== null
                        ? state$.value.formula[payload.tabId].fandeck.id
                        : null,
                    null,
                    null,
                    payload.formulaCode,
                    state$.value.searchPanel[payload.tabId].local,
                    state$.value.settings.fts,
                    state$.value.settings.searchPanelOrderByDistance,
                    true,
                    handlers.GET_COLOR_CODES_WITH_OFFSET,
                    payload.formulaLimit,
                    payload.formulaOffset,
                    "code"
                )
            ];
        })
    );

const reloadColorListByNameAndCode = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.RELOAD_FORMULA_LIST_SEARCH_BY_NAME_AND_CODE),
        switchMap(({payload}) => {
            if (state$.value.searchPanel[payload.tabId].type === componentTypes.SEARCH_PANEL_COLOR_PALETTE) {
                return [
                    getActions.browseColorsByParams(
                        payload.tabId,
                        state$.value.formula[payload.tabId].product !== null
                            ? state$.value.formula[payload.tabId].product.id
                            : null,
                        state$.value.formula[payload.tabId].fandeck !== null
                            ? state$.value.formula[payload.tabId].fandeck.id
                            : null,
                        state$.value.settings.colorTabOrderBy,
                        state$.value.settings.colorTabCompatibleOnly,
                        state$.value.settings.colorPreviewAsImage,
                        state$.value.settings.colorTabRowCount,
                        payload.formulaOffset,
                        handlers.BROWSE_CIF_WITH_OFFSET
                    )
                ];
            }

            if (state$.value.searchPanel[payload.tabId].type === componentTypes.SEARCH_PANEL_COLOR_SEARCH) {
                return [
                    getActions.getColorsByParams(
                        payload.tabId,
                        null,
                        state$.value.formula[payload.tabId].fandeck !== null
                            ? state$.value.formula[payload.tabId].fandeck.id
                            : null,
                        null,
                        payload.formulaText,
                        payload.formulaText,
                        state$.value.searchPanel[payload.tabId].local,
                        state$.value.settings.fts,
                        state$.value.settings.searchPanelOrderByDistance,
                        false,
                        handlers.GET_COLORS_WITH_OFFSET,
                        payload.formulaLimit,
                        payload.formulaOffset,
                        state$.value.searchPanel[payload.tabId].formulaListOrder,
                        state$.value.searchPanel[payload.tabId].formulaListAsc
                    )
                ];
            }

            if (state$.value.settings.searchPanelBase || state$.value.settings.searchPanelBipp) {
                return [
                    getActions.getColorsByParams(
                        payload.tabId,
                        state$.value.formula[payload.tabId].product !== null
                            ? state$.value.formula[payload.tabId].product.id
                            : null,
                        state$.value.formula[payload.tabId].fandeck !== null
                            ? state$.value.formula[payload.tabId].fandeck.id
                            : null,
                        state$.value.formula[payload.tabId].base !== null
                            ? state$.value.formula[payload.tabId].base.displayName
                            : null,
                        payload.formulaText,
                        payload.formulaText,
                        state$.value.searchPanel[payload.tabId].local,
                        state$.value.settings.fts,
                        state$.value.settings.searchPanelOrderByDistance,
                        true,
                        handlers.GET_COLORS_WITH_OFFSET,
                        payload.formulaLimit,
                        payload.formulaOffset,
                        state$.value.searchPanel[payload.tabId].formulaListOrder,
                        state$.value.searchPanel[payload.tabId].formulaListAsc
                    )
                ];
            }

            return [
                getActions.getColorsByParams(
                    payload.tabId,
                    state$.value.formula[payload.tabId].product !== null
                        ? state$.value.formula[payload.tabId].product.id
                        : null,
                    state$.value.formula[payload.tabId].fandeck !== null
                        ? state$.value.formula[payload.tabId].fandeck.id
                        : null,
                    null,
                    payload.formulaText,
                    payload.formulaText,
                    state$.value.searchPanel[payload.tabId].local,
                    state$.value.settings.fts,
                    state$.value.settings.searchPanelOrderByDistance,
                    true,
                    handlers.GET_COLORS_WITH_OFFSET,
                    payload.formulaLimit,
                    payload.formulaOffset,
                    state$.value.searchPanel[payload.tabId].formulaListOrder,
                    state$.value.searchPanel[payload.tabId].formulaListAsc
                )
            ];
        })
    );

const filterColorList = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.FILTER_FORMULA_LIST),
        switchMap(({payload}) => {
            if (
                state$.value.searchPanel[payload.tabId] === undefined ||
                state$.value.formula[payload.tabId] === undefined
            ) {
                return [];
            }

            if (state$.value.searchPanel[payload.tabId].type === componentTypes.SEARCH_PANEL_COLOR_PALETTE) {
                if (state$.value.formula[payload.tabId].fandeck !== null) {
                    return [
                        getActions.browseColorsByParams(
                            payload.tabId,
                            state$.value.formula[payload.tabId].product !== null
                                ? state$.value.formula[payload.tabId].product.id
                                : null,
                            state$.value.formula[payload.tabId].fandeck.id,
                            state$.value.settings.colorTabOrderBy,
                            state$.value.settings.colorTabCompatibleOnly,
                            state$.value.settings.colorPreviewAsImage,
                            config.NO_LIMIT
                        ),
                        {
                            type: actionTypes.SERVER_REQUEST,
                            payload: {
                                type: methods.METHOD_GENERATE_FANDECK_PREVIEW,
                                params: {
                                    productId:
                                        state$.value.formula[payload.tabId].product !== null
                                            ? state$.value.formula[payload.tabId].product.id
                                            : null,
                                    fandeckId: state$.value.formula[payload.tabId].fandeck.id,
                                    orderBy: state$.value.settings.colorTabOrderBy,
                                    compOnly: state$.value.settings.colorTabCompatibleOnly
                                }
                            },
                            method: handlers.GENERATE_FANDECK_PREVIEW,
                            tabId: payload.tabId
                        }
                    ];
                }

                return [
                    getActions.browseColorsByParams(
                        payload.tabId,
                        state$.value.formula[payload.tabId].product !== null
                            ? state$.value.formula[payload.tabId].product.id
                            : null,
                        null,
                        state$.value.settings.colorTabOrderBy,
                        state$.value.settings.colorTabCompatibleOnly,
                        state$.value.settings.colorPreviewAsImage,
                        state$.value.settings.colorTabRowCount
                    )
                ];
            }

            let formulaNameInput = "";
            let formulaCodeInput = "";
            if (state$.value.formula[payload.tabId].color === null) {
                formulaNameInput = state$.value.searchPanel[payload.tabId].formulaNameInput;
                formulaCodeInput = state$.value.searchPanel[payload.tabId].formulaCodeInput;
            } else if (state$.value.searchPanel[payload.tabId].formulaNameFilter.trim().length) {
                formulaNameInput = state$.value.searchPanel[payload.tabId].formulaNameFilter.trim();
            }

            if (state$.value.searchPanel[payload.tabId].type === componentTypes.SEARCH_PANEL_COLOR_SEARCH) {
                return [
                    getActions.getColorsByParams(
                        payload.tabId,
                        null,
                        state$.value.formula[payload.tabId].fandeck !== null
                            ? state$.value.formula[payload.tabId].fandeck.id
                            : null,
                        null,
                        formulaNameInput,
                        null,
                        state$.value.searchPanel[payload.tabId].local,
                        state$.value.settings.fts,
                        state$.value.settings.searchPanelOrderByDistance,
                        false,
                        payload.autoselect ? handlers.GET_COLORS_WITH_AUTOSELECT : handlers.GET_COLORS_BY_PARAMS,
                        config.MAX_FORMULA_LIMIT,
                        0,
                        state$.value.searchPanel[payload.tabId].formulaListOrder,
                        state$.value.searchPanel[payload.tabId].formulaListAsc
                    )
                ];
            }

            let requests = [];

            if (state$.value.settings.searchPanelBase || state$.value.settings.searchPanelBipp) {
                requests.push(
                    getActions.getColorsByParams(
                        payload.tabId,
                        state$.value.formula[payload.tabId].product !== null
                            ? state$.value.formula[payload.tabId].product.id
                            : null,
                        state$.value.formula[payload.tabId].fandeck !== null
                            ? state$.value.formula[payload.tabId].fandeck.id
                            : null,
                        state$.value.formula[payload.tabId].base !== null
                            ? state$.value.formula[payload.tabId].base.displayName
                            : null,
                        formulaNameInput,
                        null,
                        state$.value.searchPanel[payload.tabId].local,
                        state$.value.settings.fts,
                        state$.value.settings.searchPanelOrderByDistance,
                        true,
                        payload.autoselect ? handlers.GET_COLORS_WITH_AUTOSELECT : handlers.GET_COLORS_BY_PARAMS,
                        config.MAX_FORMULA_LIMIT,
                        0,
                        state$.value.searchPanel[payload.tabId].formulaListOrder,
                        state$.value.searchPanel[payload.tabId].formulaListAsc
                    )
                );
            } else {
                requests.push(
                    getActions.getColorsByParams(
                        payload.tabId,
                        state$.value.formula[payload.tabId].product !== null
                            ? state$.value.formula[payload.tabId].product.id
                            : null,
                        state$.value.formula[payload.tabId].fandeck !== null
                            ? state$.value.formula[payload.tabId].fandeck.id
                            : null,
                        null,
                        formulaNameInput,
                        null,
                        state$.value.searchPanel[payload.tabId].local,
                        state$.value.settings.fts,
                        state$.value.settings.searchPanelOrderByDistance,
                        true,
                        payload.autoselect ? handlers.GET_COLORS_WITH_AUTOSELECT : handlers.GET_COLORS_BY_PARAMS,
                        config.MAX_FORMULA_LIMIT,
                        0,
                        state$.value.searchPanel[payload.tabId].formulaListOrder,
                        state$.value.searchPanel[payload.tabId].formulaListAsc
                    )
                );
            }

            if (state$.value.settings.formulaCode && !state$.value.settings.blendFormulaAndCode) {
                if (state$.value.settings.searchPanelBase || state$.value.settings.searchPanelBipp) {
                    requests.push(
                        getActions.getColorsByParams(
                            payload.tabId,
                            state$.value.formula[payload.tabId].product !== null
                                ? state$.value.formula[payload.tabId].product.id
                                : null,
                            state$.value.formula[payload.tabId].fandeck !== null
                                ? state$.value.formula[payload.tabId].fandeck.id
                                : null,
                            state$.value.formula[payload.tabId].base !== null
                                ? state$.value.formula[payload.tabId].base.displayName
                                : null,
                            null,
                            formulaCodeInput,
                            state$.value.searchPanel[payload.tabId].local,
                            state$.value.settings.fts,
                            state$.value.settings.searchPanelOrderByDistance,
                            true,
                            handlers.GET_COLOR_CODES_BY_PARAMS,
                            config.MAX_FORMULA_LIMIT,
                            0,
                            "code"
                        )
                    );
                } else {
                    requests.push(
                        getActions.getColorsByParams(
                            payload.tabId,
                            state$.value.formula[payload.tabId].product !== null
                                ? state$.value.formula[payload.tabId].product.id
                                : null,
                            state$.value.formula[payload.tabId].fandeck !== null
                                ? state$.value.formula[payload.tabId].fandeck.id
                                : null,
                            null,
                            null,
                            formulaCodeInput,
                            state$.value.searchPanel[payload.tabId].local,
                            state$.value.settings.fts,
                            state$.value.settings.searchPanelOrderByDistance,
                            true,
                            handlers.GET_COLOR_CODES_BY_PARAMS,
                            config.MAX_FORMULA_LIMIT,
                            0,
                            "code"
                        )
                    );
                }
            }

            return requests;
        })
    );

const filterColorListByName = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.FILTER_FORMULA_LIST_BY_NAME),
        switchMap(({payload}) => {
            if (
                state$.value.searchPanel[payload.tabId] === undefined ||
                state$.value.formula[payload.tabId] === undefined
            ) {
                return [];
            }

            if (state$.value.searchPanel[payload.tabId].type === componentTypes.SEARCH_PANEL_COLOR_SEARCH) {
                return [
                    getActions.getColorsByParams(
                        payload.tabId,
                        null,
                        state$.value.formula[payload.tabId].fandeck !== null
                            ? state$.value.formula[payload.tabId].fandeck.id
                            : null,
                        null,
                        payload.formulaName,
                        null,
                        state$.value.searchPanel[payload.tabId].local,
                        state$.value.settings.fts,
                        state$.value.settings.searchPanelOrderByDistance,
                        false
                    )
                ];
            }

            if (state$.value.settings.searchPanelBase || state$.value.settings.searchPanelBipp) {
                return [
                    getActions.getColorsByParams(
                        payload.tabId,
                        state$.value.formula[payload.tabId].product !== null
                            ? state$.value.formula[payload.tabId].product.id
                            : null,
                        state$.value.formula[payload.tabId].fandeck !== null
                            ? state$.value.formula[payload.tabId].fandeck.id
                            : null,
                        state$.value.formula[payload.tabId].base !== null
                            ? state$.value.formula[payload.tabId].base.displayName
                            : null,
                        payload.formulaName,
                        null,
                        state$.value.searchPanel[payload.tabId].local,
                        state$.value.settings.fts,
                        state$.value.settings.searchPanelOrderByDistance
                    )
                ];
            }

            return [
                getActions.getColorsByParams(
                    payload.tabId,
                    state$.value.formula[payload.tabId].product !== null
                        ? state$.value.formula[payload.tabId].product.id
                        : null,
                    state$.value.formula[payload.tabId].fandeck !== null
                        ? state$.value.formula[payload.tabId].fandeck.id
                        : null,
                    null,
                    payload.formulaName,
                    null,
                    state$.value.searchPanel[payload.tabId].local,
                    state$.value.settings.fts,
                    state$.value.settings.searchPanelOrderByDistance
                )
            ];
        })
    );

const filterColorListByCode = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.FILTER_FORMULA_LIST_BY_CODE),
        switchMap(({payload}) => {
            if (
                state$.value.searchPanel[payload.tabId] === undefined ||
                state$.value.formula[payload.tabId] === undefined
            ) {
                return [];
            }

            if (state$.value.settings.searchPanelBase || state$.value.settings.searchPanelBipp) {
                return [
                    getActions.getColorsByParams(
                        payload.tabId,
                        state$.value.formula[payload.tabId].product !== null
                            ? state$.value.formula[payload.tabId].product.id
                            : null,
                        state$.value.formula[payload.tabId].fandeck !== null
                            ? state$.value.formula[payload.tabId].fandeck.id
                            : null,
                        state$.value.formula[payload.tabId].base !== null
                            ? state$.value.formula[payload.tabId].base.displayName
                            : null,
                        null,
                        payload.formulaCode,
                        state$.value.searchPanel[payload.tabId].local,
                        state$.value.settings.fts,
                        state$.value.settings.searchPanelOrderByDistance,
                        true,
                        handlers.GET_COLOR_CODES_BY_PARAMS,
                        config.MAX_FORMULA_LIMIT,
                        0,
                        "code"
                    )
                ];
            }

            return [
                getActions.getColorsByParams(
                    payload.tabId,
                    state$.value.formula[payload.tabId].product !== null
                        ? state$.value.formula[payload.tabId].product.id
                        : null,
                    state$.value.formula[payload.tabId].fandeck !== null
                        ? state$.value.formula[payload.tabId].fandeck.id
                        : null,
                    null,
                    null,
                    payload.formulaCode,
                    state$.value.searchPanel[payload.tabId].local,
                    state$.value.settings.fts,
                    state$.value.settings.searchPanelOrderByDistance,
                    true,
                    handlers.GET_COLOR_CODES_BY_PARAMS,
                    config.MAX_FORMULA_LIMIT,
                    0,
                    "code"
                )
            ];
        })
    );

const filterColorListByNameAndCode = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.FILTER_FORMULA_LIST_BY_NAME_AND_CODE),
        switchMap(({payload}) => {
            if (
                state$.value.searchPanel[payload.tabId] === undefined ||
                state$.value.formula[payload.tabId] === undefined
            ) {
                return [];
            }

            if (state$.value.searchPanel[payload.tabId].type === componentTypes.SEARCH_PANEL_COLOR_SEARCH) {
                return [
                    getActions.getColorsByParams(
                        payload.tabId,
                        null,
                        state$.value.formula[payload.tabId].fandeck !== null
                            ? state$.value.formula[payload.tabId].fandeck.id
                            : null,
                        null,
                        payload.formulaText,
                        payload.formulaText,
                        state$.value.searchPanel[payload.tabId].local,
                        state$.value.settings.fts,
                        state$.value.settings.searchPanelOrderByDistance,
                        false,
                        handlers.GET_COLORS_BY_PARAMS,
                        config.MAX_FORMULA_LIMIT,
                        0,
                        state$.value.searchPanel[payload.tabId].formulaListOrder,
                        state$.value.searchPanel[payload.tabId].formulaListAsc
                    )
                ];
            }

            if (state$.value.settings.searchPanelBase || state$.value.settings.searchPanelBipp) {
                return [
                    getActions.getColorsByParams(
                        payload.tabId,
                        state$.value.formula[payload.tabId].product !== null
                            ? state$.value.formula[payload.tabId].product.id
                            : null,
                        state$.value.formula[payload.tabId].fandeck !== null
                            ? state$.value.formula[payload.tabId].fandeck.id
                            : null,
                        state$.value.formula[payload.tabId].base !== null
                            ? state$.value.formula[payload.tabId].base.displayName
                            : null,
                        payload.formulaText,
                        payload.formulaText,
                        state$.value.searchPanel[payload.tabId].local,
                        state$.value.settings.fts,
                        state$.value.settings.searchPanelOrderByDistance,
                        true,
                        handlers.GET_COLORS_BY_PARAMS,
                        config.MAX_FORMULA_LIMIT,
                        0,
                        state$.value.searchPanel[payload.tabId].formulaListOrder,
                        state$.value.searchPanel[payload.tabId].formulaListAsc
                    )
                ];
            }

            return [
                getActions.getColorsByParams(
                    payload.tabId,
                    state$.value.formula[payload.tabId].product !== null
                        ? state$.value.formula[payload.tabId].product.id
                        : null,
                    state$.value.formula[payload.tabId].fandeck !== null
                        ? state$.value.formula[payload.tabId].fandeck.id
                        : null,
                    null,
                    payload.formulaText,
                    payload.formulaText,
                    state$.value.searchPanel[payload.tabId].local,
                    state$.value.settings.fts,
                    state$.value.settings.searchPanelOrderByDistance,
                    true,
                    handlers.GET_COLORS_BY_PARAMS,
                    config.MAX_FORMULA_LIMIT,
                    0,
                    state$.value.searchPanel[payload.tabId].formulaListOrder,
                    state$.value.searchPanel[payload.tabId].formulaListAsc
                )
            ];
        })
    );

const reloadBaseList = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.RELOAD_BASE_LIST_SEARCH),
        switchMap(({payload}) => {
            return [
                getActions.getBasesByParams(
                    payload.tabId,
                    state$.value.formula[payload.tabId].product === null
                        ? null
                        : state$.value.formula[payload.tabId].product.id,
                    state$.value.formula[payload.tabId].fandeck === null
                        ? null
                        : state$.value.formula[payload.tabId].fandeck.id,
                    state$.value.formula[payload.tabId].color === null
                        ? null
                        : state$.value.formula[payload.tabId].color.id,
                    payload.baseName,
                    state$.value.searchPanel[payload.tabId].local,
                    state$.value.settings.fts,
                    handlers.GET_BASES_WITH_OFFSET,
                    payload.baseLimit,
                    payload.baseOffset
                )
            ];
        })
    );

const filterBaseList = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.FILTER_BASE_LIST),
        switchMap(({payload}) => {
            if (
                state$.value.searchPanel[payload.tabId] === undefined ||
                state$.value.formula[payload.tabId] === undefined ||
                !service.isSearchPanelBaseFilterActive(
                    state$.value.searchPanel[payload.tabId].type,
                    state$.value.settings.searchPanelBase
                )
            ) {
                return [];
            }

            let responseHandler = handlers.GET_BASES_BY_PARAMS;
            if (payload.autoselect) {
                responseHandler = handlers.GET_BASES_WITH_AUTOSELECT;
            }

            let baseNameInput = "";
            if (state$.value.formula[payload.tabId].base === null) {
                baseNameInput = state$.value.searchPanel[payload.tabId].baseNameInput;
            }

            return [
                getActions.getBasesByParams(
                    payload.tabId,
                    state$.value.formula[payload.tabId].product === null
                        ? null
                        : state$.value.formula[payload.tabId].product.id,
                    state$.value.formula[payload.tabId].fandeck === null
                        ? null
                        : state$.value.formula[payload.tabId].fandeck.id,
                    state$.value.formula[payload.tabId].color === null
                        ? null
                        : state$.value.formula[payload.tabId].color.id,
                    baseNameInput,
                    state$.value.searchPanel[payload.tabId].local,
                    state$.value.settings.fts,
                    responseHandler
                )
            ];
        })
    );

const filterBaseListByName = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.FILTER_BASE_LIST_BY_NAME),
        switchMap(({payload}) => {
            if (
                state$.value.searchPanel[payload.tabId] === undefined ||
                state$.value.formula[payload.tabId] === undefined
            ) {
                return [];
            }

            return [
                getActions.getBasesByParams(
                    payload.tabId,
                    state$.value.formula[payload.tabId].product === null
                        ? null
                        : state$.value.formula[payload.tabId].product.id,
                    state$.value.formula[payload.tabId].fandeck === null
                        ? null
                        : state$.value.formula[payload.tabId].fandeck.id,
                    state$.value.formula[payload.tabId].color === null
                        ? null
                        : state$.value.formula[payload.tabId].color.id,
                    payload.baseName,
                    state$.value.searchPanel[payload.tabId].local,
                    state$.value.settings.fts,
                    handlers.GET_BASES_BY_PARAMS,
                    payload.baseLimit,
                    payload.baseOffset
                )
            ];
        })
    );

const reloadBippList = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.RELOAD_BIPP_LIST_SEARCH),
        switchMap(({payload}) => {
            return [
                getActions.getBippsByParams(
                    payload.tabId,
                    state$.value.formula[payload.tabId].product === null || !state$.value.settings.searchPanelProduct
                        ? null
                        : state$.value.formula[payload.tabId].product.id,
                    state$.value.formula[payload.tabId].fandeck === null
                        ? null
                        : state$.value.formula[payload.tabId].fandeck.id,
                    state$.value.formula[payload.tabId].color === null
                        ? null
                        : state$.value.formula[payload.tabId].color.id,
                    state$.value.formula[payload.tabId].base === null || !state$.value.settings.searchPanelBase
                        ? null
                        : state$.value.formula[payload.tabId].base.displayName,
                    payload.bippName,
                    state$.value.searchPanel[payload.tabId].local,
                    state$.value.settings.fts,
                    handlers.GET_BIPP_WITH_OFFSET,
                    payload.bippLimit,
                    payload.bippOffset
                )
            ];
        })
    );

const filterBippList = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.FILTER_BIPP_LIST),
        switchMap(({payload}) => {
            if (
                state$.value.searchPanel[payload.tabId] === undefined ||
                state$.value.formula[payload.tabId] === undefined ||
                !service.isSearchPanelBippFilterActive(
                    state$.value.searchPanel[payload.tabId].type,
                    state$.value.settings.searchPanelBipp
                )
            ) {
                return [];
            }

            let responseHandler = handlers.GET_BIPP_BY_PARAMS;
            if (payload.autoselect) {
                responseHandler = handlers.GET_BIPP_WITH_AUTOSELECT;
            }

            let bippNameInput = "";
            if (state$.value.formula[payload.tabId].bipp === null) {
                bippNameInput = state$.value.searchPanel[payload.tabId].bippNameInput;
            }

            return [
                getActions.getBippsByParams(
                    payload.tabId,
                    state$.value.formula[payload.tabId].product === null || !state$.value.settings.searchPanelProduct
                        ? null
                        : state$.value.formula[payload.tabId].product.id,
                    state$.value.formula[payload.tabId].fandeck === null
                        ? null
                        : state$.value.formula[payload.tabId].fandeck.id,
                    state$.value.formula[payload.tabId].color === null
                        ? null
                        : state$.value.formula[payload.tabId].color.id,
                    state$.value.formula[payload.tabId].base === null || !state$.value.settings.searchPanelBase
                        ? null
                        : state$.value.formula[payload.tabId].base.displayName,
                    bippNameInput,
                    state$.value.searchPanel[payload.tabId].local,
                    state$.value.settings.fts,
                    responseHandler
                )
            ];
        })
    );

const filterBippListByName = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.FILTER_BIPP_LIST_BY_NAME),
        switchMap(({payload}) => {
            if (
                state$.value.searchPanel[payload.tabId] === undefined ||
                state$.value.formula[payload.tabId] === undefined
            ) {
                return [];
            }

            return [
                getActions.getBippsByParams(
                    payload.tabId,
                    state$.value.formula[payload.tabId].product === null || !state$.value.settings.searchPanelProduct
                        ? null
                        : state$.value.formula[payload.tabId].product.id,
                    state$.value.formula[payload.tabId].fandeck === null
                        ? null
                        : state$.value.formula[payload.tabId].fandeck.id,
                    state$.value.formula[payload.tabId].color === null
                        ? null
                        : state$.value.formula[payload.tabId].color.id,
                    state$.value.formula[payload.tabId].base === null || !state$.value.settings.searchPanelBase
                        ? null
                        : state$.value.formula[payload.tabId].base.displayName,
                    payload.bippName,
                    state$.value.searchPanel[payload.tabId].local,
                    state$.value.settings.fts,
                    handlers.GET_BIPP_BY_PARAMS,
                    payload.bippLimit,
                    payload.bippOffset
                )
            ];
        })
    );

const removeSelected = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.SEARCH_PANEL_REMOVE_SELECTED),
        switchMap(({payload}) => {
            if (
                state$.value.searchPanel[payload.tabId] === undefined ||
                state$.value.formula[payload.tabId] === undefined
            ) {
                return [];
            }

            return [
                {
                    type: actionTypes.UPDATE_SEARCH_PANEL,
                    payload: service.getStateAfterRemoveSelectedItem(
                        payload.tabId,
                        payload.panel,
                        state$.value.searchPanel[payload.tabId],
                        state$.value.formula[payload.tabId],
                        state$.value.settings.searchPanelOrder,
                        state$.value.settings.formulaCode,
                        state$.value.settings.searchPanelProduct,
                        state$.value.settings.searchPanelBase,
                        state$.value.settings.intergammaColorFandeckWizard,
                        state$.value.module.gammaSoftware
                    )
                },
                {
                    type: actionTypes.UPDATE_TAB_NAME,
                    payload: {
                        tabId: payload.tabId,
                        color: null
                    }
                }
            ];
        })
    );

const setSearchPanel = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.SET_SEARCH_PANEL),
        switchMap(({payload}) => {
            let searchPanelRequests = [
                {
                    type: actionTypes.FILTER_PRODUCT_LIST,
                    payload: {
                        tabId: payload.tabId,
                        autoselect: false
                    }
                },
                {
                    type: actionTypes.FILTER_FANDECK_LIST,
                    payload: {
                        tabId: payload.tabId,
                        autoselect: false
                    }
                },
                {
                    type: actionTypes.FILTER_FORMULA_LIST,
                    payload: {
                        tabId: payload.tabId,
                        autoselect: false
                    }
                },
                {
                    type: actionTypes.FILTER_BASE_LIST,
                    payload: {
                        tabId: payload.tabId,
                        autoselect: false
                    }
                },
                {
                    type: actionTypes.FILTER_BIPP_LIST,
                    payload: {
                        tabId: payload.tabId,
                        autoselect: false
                    }
                }
            ];

            if (state$.value.settings.blendFormulaAndCode && payload.color !== null) {
                searchPanelRequests.unshift({
                    type: actionTypes.SEARCH_PANEL_SET_FORMULA_NAME_INPUT,
                    payload: {
                        tabId: payload.tabId,
                        formulaName: payload.color.getLongDisplayName(state$.value.settings.formulaCodePrimary)
                    }
                });
            }

            if (!service.isSearchPanelValid(state$.value.formula[payload.tabId])) {
                return searchPanelRequests;
            }

            searchPanelRequests.unshift(
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_CALCULATED_FORMULA,
                        params: {
                            productId: payload.product.id,
                            fandeckId: payload.fandeck.id,
                            colorId: payload.color.id,
                            baseId: payload.base === null ? null : payload.base.id,
                            baseInProductPackageId: payload.bipp === null ? null : payload.bipp.id,
                            isLocal: payload.color.local
                        }
                    },
                    method: handlers.GET_CALCULATED_FORMULA,
                    tabId: payload.tabId
                },
                {
                    type: actionTypes.UPDATE_TAB_NAME,
                    payload: {
                        tabId: payload.tabId,
                        color: payload.color.updateLongDisplayName(
                            state$.value.settings.blendFormulaAndCode,
                            state$.value.settings.formulaCodePrimary
                        )
                    }
                }
            );

            return searchPanelRequests;
        })
    );

const constructSearchPanel = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.SEARCH_PANEL_SET_ACTIVE_SELECT),
        switchMap(({payload}) => {
            if (payload.activeSelect === "" || state$.value.searchPanel[payload.tabId] === undefined) {
                return [];
            }

            if (state$.value.settings.searchPanelDropDownCalculate) {
                service.setSelectBoxHeight(
                    payload.tabId,
                    state$.value.searchPanel[payload.tabId],
                    payload.activeSelect
                );
            }

            if (state$.value.searchPanel[payload.tabId].init) {
                return [];
            }

            let reloadRequests = [];

            if (state$.value.searchPanel[payload.tabId].productList.length === 0) {
                reloadRequests.push({
                    type: actionTypes.FILTER_PRODUCT_LIST,
                    payload: {
                        tabId: payload.tabId,
                        autoselect: false
                    }
                });
            }

            if (state$.value.searchPanel[payload.tabId].fandeckList.length === 0) {
                reloadRequests.push({
                    type: actionTypes.FILTER_FANDECK_LIST,
                    payload: {
                        tabId: payload.tabId,
                        autoselect: false
                    }
                });
            }

            if (state$.value.searchPanel[payload.tabId].formulaList.length === 0) {
                reloadRequests.push({
                    type: actionTypes.FILTER_FORMULA_LIST,
                    payload: {
                        tabId: payload.tabId,
                        autoselect: false
                    }
                });
            }

            if (state$.value.searchPanel[payload.tabId].baseList.length === 0) {
                reloadRequests.push({
                    type: actionTypes.FILTER_BASE_LIST,
                    payload: {
                        tabId: payload.tabId,
                        autoselect: false
                    }
                });
            }

            if (state$.value.searchPanel[payload.tabId].bippList.length === 0) {
                reloadRequests.push({
                    type: actionTypes.FILTER_BIPP_LIST,
                    payload: {
                        tabId: payload.tabId,
                        autoselect: false
                    }
                });
            }

            return reloadRequests;
        })
    );

const autoselectProduct = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.SAVE_PRODUCT_LIST_SEARCH_WITH_AUTOSELECT),
        switchMap(({payload}) => {
            if (
                state$.value.searchPanel[payload.tabId] === undefined ||
                state$.value.searchPanel[payload.tabId].productList.length !== 1 ||
                state$.value.searchPanel[payload.tabId].productList[0].items.length !== 1 ||
                state$.value.formula[payload.tabId] === undefined ||
                state$.value.formula[payload.tabId].product !== null
            ) {
                return [];
            }

            return [
                {
                    type: actionTypes.AUTOSELECT_PRODUCT,
                    payload: {
                        tabId: payload.tabId,
                        product: state$.value.searchPanel[payload.tabId].productList[0].items[0]
                    }
                }
            ];
        })
    );

const autoselectFandeck = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.SAVE_FANDECK_LIST_SEARCH_WITH_AUTOSELECT),
        switchMap(({payload}) => {
            if (
                state$.value.searchPanel[payload.tabId] === undefined ||
                state$.value.searchPanel[payload.tabId].fandeckList.length !== 1 ||
                state$.value.searchPanel[payload.tabId].fandeckList[0].items.length !== 1 ||
                state$.value.formula[payload.tabId] === undefined ||
                state$.value.formula[payload.tabId].fandeck !== null
            ) {
                return [];
            }

            if (state$.value.formula[payload.tabId].color === null) {
                return [
                    {
                        type: actionTypes.AUTOSELECT_FANDECK,
                        payload: {
                            tabId: payload.tabId,
                            fandeck: state$.value.searchPanel[payload.tabId].fandeckList[0].items[0]
                        }
                    }
                ];
            }

            return [
                {
                    type: actionTypes.AUTOSELECT_FANDECK,
                    payload: {
                        tabId: payload.tabId,
                        fandeck: state$.value.searchPanel[payload.tabId].fandeckList[0].items[0]
                    }
                },
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_COLOR_IN_FANDECK,
                        params: {
                            fandeckId: state$.value.searchPanel[payload.tabId].fandeckList[0].items[0].id,
                            colorId: state$.value.formula[payload.tabId].color.id,
                            isLocal: state$.value.formula[payload.tabId].color.local
                        }
                    },
                    method: handlers.GET_COLOR_IN_FANDECK,
                    tabId: payload.tabId
                }
            ];
        })
    );

const autoselectFormula = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.SAVE_FORMULA_LIST_SEARCH_WITH_AUTOSELECT),
        switchMap(({payload}) => {
            if (
                state$.value.searchPanel[payload.tabId] === undefined ||
                state$.value.searchPanel[payload.tabId].formulaList.length !== 1 ||
                state$.value.formula[payload.tabId] === undefined ||
                state$.value.formula[payload.tabId].formula !== null
            ) {
                if (
                    state$.value.searchPanel[payload.tabId] !== undefined &&
                    state$.value.searchPanel[payload.tabId].type === componentTypes.SEARCH_PANEL_COLOR_SEARCH &&
                    state$.value.searchPanel[payload.tabId].refresh === actionTypes.SELECT_FANDECK &&
                    state$.value.searchPanel[payload.tabId].formulaList.length === 0
                ) {
                    return [messageHelper.getInfoMessageAction("flashMessage.emptyFandeck")];
                }

                return [];
            }

            let color = state$.value.searchPanel[payload.tabId].formulaList[0];

            if (state$.value.settings.blendFormulaAndCode) {
                return [
                    {
                        type: actionTypes.AUTOSELECT_FORMULA,
                        payload: {
                            tabId: payload.tabId,
                            color: color,
                            formulaNameInput: color.getLongDisplayName(state$.value.settings.formulaCodePrimary)
                        }
                    }
                ];
            }

            return [
                {
                    type: actionTypes.AUTOSELECT_FORMULA,
                    payload: {
                        tabId: payload.tabId,
                        color: color,
                        formulaNameInput: color.displayName
                    }
                }
            ];
        })
    );

const autoselectBase = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.SAVE_BASE_LIST_SEARCH_WITH_AUTOSELECT),
        switchMap(({payload}) => {
            if (
                state$.value.searchPanel[payload.tabId] === undefined ||
                state$.value.searchPanel[payload.tabId].baseList.length !== 1 ||
                state$.value.formula[payload.tabId] === undefined ||
                state$.value.formula[payload.tabId].base !== null
            ) {
                return [];
            }

            return [
                {
                    type: actionTypes.AUTOSELECT_BASE,
                    payload: {
                        tabId: payload.tabId,
                        base: state$.value.searchPanel[payload.tabId].baseList[0]
                    }
                }
            ];
        })
    );

const autoselectBipp = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.SAVE_BIPP_LIST_SEARCH_WITH_AUTOSELECT),
        switchMap(({payload}) => {
            if (
                state$.value.searchPanel[payload.tabId] === undefined ||
                state$.value.searchPanel[payload.tabId].bippList.length !== 1 ||
                state$.value.formula[payload.tabId] === undefined ||
                state$.value.formula[payload.tabId].bipp !== null
            ) {
                return [];
            }

            return [
                {
                    type: actionTypes.AUTOSELECT_BIPP,
                    payload: {
                        tabId: payload.tabId,
                        bipp: state$.value.searchPanel[payload.tabId].bippList[0]
                    }
                }
            ];
        })
    );

const checkAutoselect = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.SEARCH_PANEL_CHECK_AUTOSELECT),
        switchMap(({payload}) => {
            if (
                state$.value.searchPanel[payload.tabId] === undefined ||
                state$.value.formula[payload.tabId] === undefined
            ) {
                return [
                    {
                        type: actionTypes.SET_AUTOMATIC_MAIN_TAB_SCENE,
                        payload: {
                            tabId: payload.tabId
                        }
                    }
                ];
            }

            let actions = [];
            let autoselect = false;
            let searchPanelData = {
                tabId: payload.tabId,
                product: state$.value.formula[payload.tabId].product,
                fandeck: state$.value.formula[payload.tabId].fandeck,
                color: state$.value.formula[payload.tabId].color,
                base: state$.value.formula[payload.tabId].base,
                bipp: state$.value.formula[payload.tabId].bipp
            };

            if (
                state$.value.searchPanel[payload.tabId].productList.length === 1 &&
                state$.value.searchPanel[payload.tabId].productList[0].items.length === 1 &&
                state$.value.formula[payload.tabId].product === null
            ) {
                searchPanelData.product = state$.value.searchPanel[payload.tabId].productList[0].items[0];
                autoselect = true;
            }

            if (
                state$.value.searchPanel[payload.tabId].fandeckList.length === 1 &&
                state$.value.searchPanel[payload.tabId].fandeckList[0].items.length === 1 &&
                state$.value.formula[payload.tabId].fandeck === null
            ) {
                searchPanelData.fandeck = state$.value.searchPanel[payload.tabId].fandeckList[0].items[0];
                autoselect = true;
            }

            if (
                state$.value.searchPanel[payload.tabId].formulaList.length === 1 &&
                state$.value.formula[payload.tabId].color === null
            ) {
                searchPanelData.fandeck = state$.value.searchPanel[payload.tabId].formulaList[0];
                autoselect = true;
            }

            if (autoselect) {
                actions.push({
                    type: actionTypes.SET_SEARCH_PANEL,
                    payload: searchPanelData
                });

                if (searchPanelData.color !== null && searchPanelData.fandeck !== null) {
                    actions.push({
                        type: actionTypes.SERVER_REQUEST,
                        payload: {
                            type: methods.METHOD_GET_COLOR_IN_FANDECK,
                            params: {
                                fandeckId: searchPanelData.fandeck.id,
                                colorId: searchPanelData.color.id,
                                isLocal: searchPanelData.color.local
                            }
                        },
                        method: handlers.GET_COLOR_IN_FANDECK,
                        tabId: payload.tabId
                    });
                }
            }

            actions.push({
                type: actionTypes.SET_AUTOMATIC_MAIN_TAB_SCENE,
                payload: {
                    tabId: payload.tabId
                }
            });

            return actions;
        })
    );

const afterAutoselect = (action$, state$) =>
    action$.pipe(
        ofType(
            actionTypes.AUTOSELECT_PRODUCT,
            actionTypes.AUTOSELECT_FANDECK,
            actionTypes.AUTOSELECT_FORMULA,
            actionTypes.AUTOSELECT_BASE,
            actionTypes.AUTOSELECT_BIPP
        ),
        switchMap(({payload}) => {
            if (state$.value.formula[payload.tabId] === undefined) {
                return [];
            }

            service.applySmartFocus(
                payload.tabId,
                state$.value.formula[payload.tabId],
                state$.value.searchPanel[payload.tabId].type,
                state$.value.settings.searchPanelOrder
            );

            let actions = [
                {
                    type: actionTypes.FILTER_PRODUCT_LIST,
                    payload: {
                        tabId: payload.tabId,
                        autoselect: false
                    }
                },
                {
                    type: actionTypes.FILTER_FANDECK_LIST,
                    payload: {
                        tabId: payload.tabId,
                        autoselect: false
                    }
                },
                {
                    type: actionTypes.FILTER_FORMULA_LIST,
                    payload: {
                        tabId: payload.tabId,
                        autoselect: false
                    }
                },
                {
                    type: actionTypes.FILTER_BASE_LIST,
                    payload: {
                        tabId: payload.tabId,
                        autoselect: false
                    }
                },
                {
                    type: actionTypes.FILTER_BIPP_LIST,
                    payload: {
                        tabId: payload.tabId,
                        autoselect: false
                    }
                }
            ];

            if (state$.value.settings.searchPanelQuickSelect) {
                actions.push({
                    type: actionTypes.SET_AUTOMATIC_MAIN_TAB_SCENE,
                    payload: {
                        tabId: payload.tabId
                    }
                });
            }

            if (
                !service.isSearchPanelValid(
                    state$.value.formula[payload.tabId],
                    state$.value.searchPanel[payload.tabId].type,
                    state$.value.settings.searchPanelOrder
                )
            ) {
                return actions;
            }

            return [
                {
                    type: actionTypes.SEARCH_PANEL_GET_FORMULA,
                    payload: {
                        tabId: payload.tabId
                    }
                },
                ...actions
            ];
        })
    );

const resetSearchPanelColor = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.RELOAD_SEARCH_PANEL_COLOR_PALETTE),
        switchMap(() => {
            let colorTabs = state$.value.navigation.tabList.filter((tab) => tab.type === componentTypes.COLOR_TAB);

            let actions = [];

            let tab;
            for (tab of colorTabs) {
                if (state$.value.searchPanel[tab.id] !== undefined || state$.value.formula[tab.id] !== undefined) {
                    actions.push(
                        getActions.browseColorsByParams(
                            tab.id,
                            state$.value.formula[tab.id].product !== null
                                ? state$.value.formula[tab.id].product.id
                                : null,
                            state$.value.formula[tab.id].fandeck !== null
                                ? state$.value.formula[tab.id].fandeck.id
                                : null,
                            state$.value.settings.colorTabOrderBy,
                            state$.value.settings.colorTabCompatibleOnly,
                            state$.value.settings.colorPreviewAsImage,
                            state$.value.formula[tab.id].fandeck !== null
                                ? config.NO_LIMIT
                                : state$.value.settings.colorTabRowCount,
                            state$.value.searchPanel[tab.id].formulaListOffset
                        )
                    );

                    if (state$.value.formula[tab.id].fandeck !== null) {
                        actions.push({
                            type: actionTypes.SERVER_REQUEST,
                            payload: {
                                type: methods.METHOD_GENERATE_FANDECK_PREVIEW,
                                params: {
                                    productId:
                                        state$.value.formula[tab.id].product !== null
                                            ? state$.value.formula[tab.id].product.id
                                            : null,
                                    fandeckId: state$.value.formula[tab.id].fandeck.id,
                                    orderBy: state$.value.settings.colorTabOrderBy,
                                    compOnly: state$.value.settings.colorTabCompatibleOnly
                                }
                            },
                            method: handlers.GENERATE_FANDECK_PREVIEW,
                            tabId: tab.id
                        });
                    }
                }
            }

            return actions;
        })
    );

const selectClosesrColorAndFandeck = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.SELECT_CLOSEST_COLOR_AND_FANDECK),
        switchMap(({payload}) => {
            if (state$.value.formula[payload.tabId] === undefined) {
                return [];
            }

            return [
                {
                    type: actionTypes.SET_SEARCH_PANEL,
                    payload: {
                        tabId: payload.tabId,
                        product: state$.value.formula[payload.tabId].product,
                        fandeck: state$.value.formula[payload.tabId].fandeck,
                        color: payload.color,
                        base: null,
                        bipp: null
                    }
                },
                {
                    type: actionTypes.ADD_NEW_MAIN_TAB,
                    payload: {
                        tabId: payload.tabId,
                        color: payload.color,
                        scene: null
                    }
                },
                {
                    type: actionTypes.NAVIGATION_CLOSE_MODAL,
                    payload: {
                        type: componentTypes.CLOSEST_COLORS_MODAL
                    }
                }
            ];
        })
    );

const selectColorPaletteFormula = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.SELECT_COLOR_PALETTE_FORMULA),
        switchMap(({payload}) => {
            if (
                !service.isSearchPanelValid(
                    state$.value.formula[payload.tabId],
                    state$.value.searchPanel[payload.tabId].type
                )
            ) {
                if (
                    state$.value.login.loggedUser.canEdit(resources.FUNCTION_COMPETITIVE_COLOR_SEARCH) &&
                    state$.value.formula[payload.tabId].color !== null &&
                    !state$.value.formula[payload.tabId].color.hasFormula &&
                    state$.value.formula[payload.tabId].color.fandeckId !== null
                ) {
                    return [searchPanelActions.getClosestColorSearch(payload.tabId)];
                }

                return [];
            }

            return [
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_CALCULATED_FORMULA,
                        params: {
                            productId:
                                state$.value.formula[payload.tabId].product === null
                                    ? null
                                    : state$.value.formula[payload.tabId].product.id,
                            fandeckId: state$.value.formula[payload.tabId].fandeck.id,
                            colorId: state$.value.formula[payload.tabId].color.id,
                            baseId:
                                state$.value.searchPanel[payload.tabId].type ===
                                    componentTypes.SEARCH_PANEL_COLOR_PALETTE ||
                                state$.value.formula[payload.tabId].base === null
                                    ? null
                                    : state$.value.formula[payload.tabId].base.id,
                            isLocal: state$.value.formula[payload.tabId].color.local
                        }
                    },
                    method: handlers.NAVIGATION_GET_CALCULATED_FORMULA,
                    tabId: payload.tabId
                }
            ];
        })
    );

const updateSearchPanel = (action$) =>
    action$.pipe(
        ofType(actionTypes.UPDATE_SEARCH_PANEL),
        switchMap(({payload}) => {
            return [
                {
                    type: actionTypes.FILTER_PRODUCT_LIST,
                    payload: {
                        tabId: payload.tabId,
                        autoselect: false
                    }
                },
                {
                    type: actionTypes.FILTER_FANDECK_LIST,
                    payload: {
                        tabId: payload.tabId,
                        autoselect: false
                    }
                },
                {
                    type: actionTypes.FILTER_FORMULA_LIST,
                    payload: {
                        tabId: payload.tabId,
                        autoselect: false
                    }
                },
                {
                    type: actionTypes.FILTER_BASE_LIST,
                    payload: {
                        tabId: payload.tabId,
                        autoselect: false
                    }
                },
                {
                    type: actionTypes.FILTER_BIPP_LIST,
                    payload: {
                        tabId: payload.tabId,
                        autoselect: false
                    }
                }
            ];
        })
    );

const focusOnNextPanel = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.SEARCH_PANEL_FOCUS_ON_NEXT_PANEL),
        switchMap(({payload}) => {
            if (state$.value.formula[payload.tabId] !== undefined) {
                service.applyFocusOnNext(
                    payload.tabId,
                    payload.panel,
                    state$.value.formula[payload.tabId],
                    state$.value.searchPanel[payload.tabId].type,
                    state$.value.settings.searchPanelOrder
                );
            }

            return [];
        })
    );

const toggleFormulaListSort = (action$) =>
    action$.pipe(
        ofType(actionTypes.SEARCH_PANEL_TOGGLE_FORMULA_NAME_SORT, actionTypes.SEARCH_PANEL_TOGGLE_FORMULA_CODE_SORT),
        switchMap(({payload}) => {
            return [
                {
                    type: actionTypes.FILTER_FORMULA_LIST,
                    payload: {
                        tabId: payload.tabId,
                        autoselect: false
                    }
                }
            ];
        })
    );

const saveFandeckListWithOffset = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.SAVE_FANDECK_LIST_SEARCH_WITH_OFFSET),
        switchMap(({payload}) => {
            if (state$.value.searchPanel[payload.tabId] && state$.value.settings.searchPanelDropDownCalculate) {
                service.setSelectBoxHeight(
                    payload.tabId,
                    state$.value.searchPanel[payload.tabId],
                    componentTypes.SEARCH_PANEL_FANDECK
                );
            }

            return [];
        })
    );

const saveFormulaListWithOffset = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.SAVE_FORMULA_LIST_SEARCH_WITH_OFFSET),
        switchMap(({payload}) => {
            if (state$.value.searchPanel[payload.tabId] && state$.value.settings.searchPanelDropDownCalculate) {
                service.setSelectBoxHeight(
                    payload.tabId,
                    state$.value.searchPanel[payload.tabId],
                    componentTypes.SEARCH_PANEL_FORMULA_NAME
                );
            }

            return [];
        })
    );

const saveFormulaCodeListWithOffset = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.SAVE_FORMULA_CODE_LIST_SEARCH_WITH_OFFSET),
        switchMap(({payload}) => {
            if (state$.value.searchPanel[payload.tabId] && state$.value.settings.searchPanelDropDownCalculate) {
                service.setSelectBoxHeight(
                    payload.tabId,
                    state$.value.searchPanel[payload.tabId],
                    componentTypes.SEARCH_PANEL_FORMULA_CODE
                );
            }

            return [];
        })
    );

const saveBaseListWithOffset = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.SAVE_BASE_LIST_SEARCH_WITH_OFFSET),
        switchMap(({payload}) => {
            if (state$.value.searchPanel[payload.tabId] && state$.value.settings.searchPanelDropDownCalculate) {
                service.setSelectBoxHeight(
                    payload.tabId,
                    state$.value.searchPanel[payload.tabId],
                    componentTypes.SEARCH_PANEL_BASE
                );
            }

            return [];
        })
    );

const saveBippListWithOffset = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.SAVE_BIPP_LIST_SEARCH_WITH_OFFSET),
        switchMap(({payload}) => {
            if (state$.value.searchPanel[payload.tabId] && state$.value.settings.searchPanelDropDownCalculate) {
                service.setSelectBoxHeight(
                    payload.tabId,
                    state$.value.searchPanel[payload.tabId],
                    componentTypes.SEARCH_PANEL_BIPP
                );
            }

            return [];
        })
    );

export default [
    searchPanelInit,
    selectProduct,
    selectFandeck,
    selectFormula,
    selectBase,
    selectBipp,
    getCalculatedFormula,
    getClosestColor,
    filterProductList,
    filterProductListByName,
    reloadFandeckList,
    filterFandeckList,
    filterFandeckListByName,
    reloadColorListByName,
    reloadColorListByCode,
    reloadColorListByNameAndCode,
    filterColorList,
    filterColorListByName,
    filterColorListByCode,
    filterColorListByNameAndCode,
    reloadBaseList,
    filterBaseList,
    filterBaseListByName,
    reloadBippList,
    filterBippList,
    filterBippListByName,
    removeSelected,
    setSearchPanel,
    constructSearchPanel,
    autoselectProduct,
    autoselectFandeck,
    autoselectFormula,
    autoselectBase,
    autoselectBipp,
    checkAutoselect,
    afterAutoselect,
    resetSearchPanelColor,
    selectClosesrColorAndFandeck,
    selectColorPaletteFormula,
    updateSearchPanel,
    focusOnNextPanel,
    toggleFormulaListSort,
    saveFandeckListWithOffset,
    saveFormulaListWithOffset,
    saveFormulaCodeListWithOffset,
    saveBaseListWithOffset,
    saveBippListWithOffset
];
